// ─── Filtre, recherche et tri de la vue Mois (module PUR) ─────────
// Séparé du moteur d'occurrences : celui-ci CALCULE l'état du mois, celui-là
// décide de ce qu'on montre et dans quel ordre. Aucun accès Firestore ni React.

export const TRIS = [
  { id: 'statut',  cle: 'finances.tri_statut' },
  { id: 'libelle', cle: 'finances.tri_libelle' },
  { id: 'montant', cle: 'finances.tri_montant' },
  { id: 'ecart',   cle: 'finances.tri_ecart' },
];

// Ordre d'urgence : ce qui réclame une action d'abord, ce qui est réglé ensuite.
// « soldée » se range avec « réalisée » : dans les deux cas l'échéance est
// close, elle ne réclame plus d'action — c'est ce que ce tri met en avant.
const RANG_STATUT = { manquante: 0, partielle: 1, avenir: 2, realisee: 3, soldee: 4, absente: 5, ignoree: 6 };

// La recherche porte sur le NOM et sur le COÛT : « edf » comme « 40,71 » ou
// « 40.7 » doivent trouver. On compare donc aussi la forme numérique brute.
const normaliser = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
const correspondMontant = (montant, q) => {
  const num = q.replace(',', '.').replace(/[^0-9.]/g, '');
  if (!num) return false;
  return String(Math.abs(Number(montant) || 0)).includes(num)
    || String(Math.round(Math.abs(Number(montant) || 0))).includes(num);
};

export function filtrerOccurrences(occurrences = [], { recherche = '', budgetIds = null, tri = 'statut' } = {}) {
  const q = normaliser(recherche).trim();
  let out = occurrences.filter(o => {
    if (budgetIds && budgetIds.length && !budgetIds.includes(o.ligne?.budgetId || null)) return false;
    if (!q) return true;
    return normaliser(o.ligne?.libelle).includes(q)
      || correspondMontant(o.prevu, q) || correspondMontant(o.reel, q);
  });
  out = [...out];
  if (tri === 'libelle') out.sort((a, b) => (a.ligne?.libelle || '').localeCompare(b.ligne?.libelle || ''));
  else if (tri === 'montant') out.sort((a, b) => Math.abs(b.prevu) - Math.abs(a.prevu));
  else if (tri === 'ecart') out.sort((a, b) => Math.abs(b.ecart) - Math.abs(a.ecart));
  else out.sort((a, b) => (RANG_STATUT[a.statut] ?? 9) - (RANG_STATUT[b.statut] ?? 9)
    || (a.ligne?.libelle || '').localeCompare(b.ligne?.libelle || ''));
  return out;
}

export function filtrerImprevus(imprevus = [], { recherche = '', budgetIds = null, tri = 'statut' } = {}) {
  const q = normaliser(recherche).trim();
  let out = imprevus.filter(op => {
    if (budgetIds && budgetIds.length && !budgetIds.includes(op.budgetId || null)) return false;
    if (!q) return true;
    return normaliser(op.libelle).includes(q) || correspondMontant(op.montant, q);
  });
  out = [...out];
  if (tri === 'libelle') out.sort((a, b) => (a.libelle || '').localeCompare(b.libelle || ''));
  else out.sort((a, b) => Math.abs(b.montant || 0) - Math.abs(a.montant || 0));   // par défaut : le plus gros d'abord
  return out;
}

// Nombre de filtres ACTIFS, pour la pastille du bouton (0 = aucun filtre).
export const nbFiltresActifs = ({ recherche = '', budgetIds = null } = {}) =>
  (recherche.trim() ? 1 : 0) + (budgetIds && budgetIds.length ? 1 : 0);

// ── Relevé d'un compte : filtre, recherche et tri ────────────────
// Les occurrences ci-dessus décrivent un PRÉVU (statut, écart) ; ici on
// manipule des opérations BRUTES, où seules comptent la date, le sens et le
// montant. D'où un jeu de tris distinct : trier un relevé par « ce qui réclame
// une action » n'aurait aucun sens.
export const TRIS_OPERATIONS = [
  { id: 'recente',  cle: 'finances.plusRecente' },
  { id: 'ancienne', cle: 'finances.plusAncienne' },
  { id: 'montant',  cle: 'finances.triMontantOp' },
  { id: 'libelle',  cle: 'finances.tri_libelle' },
];

// `sens` : 'tout' | 'revenu' | 'depense'.
// `avecHorsCalcul` : les mouvements internes (`exclu`) restent visibles par
// défaut. Les masquer d'office ferait disparaître de l'argent réellement parti
// du compte — le solde ne collerait plus avec la liste affichée.
export function filtrerOperations(operations = [], {
  recherche = '', sens = 'tout', avecHorsCalcul = true, tri = 'recente',
} = {}) {
  const q = normaliser(recherche).trim();
  const date = (op) => (op && (op.dateBanque || op.datePaiement || op.date)) || '';
  let out = operations.filter(op => {
    if (!op) return false;
    if (!avecHorsCalcul && op.exclu) return false;
    if (sens === 'revenu' && op.sens !== 'revenu') return false;
    if (sens === 'depense' && op.sens === 'revenu') return false;
    if (!q) return true;
    return normaliser(op.libelle).includes(q) || correspondMontant(op.montant, q);
  });
  out = [...out];
  if (tri === 'ancienne') out.sort((a, b) => String(date(a)).localeCompare(String(date(b))));
  else if (tri === 'montant') out.sort((a, b) => Math.abs(b.montant || 0) - Math.abs(a.montant || 0));
  else if (tri === 'libelle') out.sort((a, b) => (a.libelle || '').localeCompare(b.libelle || ''));
  else out.sort((a, b) => String(date(b)).localeCompare(String(date(a))));
  return out;
}

// Pastille du bouton filtre du relevé : la recherche a son propre champ, elle
// n'y compte donc pas — seuls les critères cachés derrière la feuille.
export const nbFiltresReleve = ({ sens = 'tout', avecHorsCalcul = true } = {}) =>
  (sens !== 'tout' ? 1 : 0) + (avecHorsCalcul ? 0 : 1);

// ── Plan d'une enveloppe (onglet Budget) ─────────────────────────
// Un plan n'a ni date de mouvement ni statut de réalisation : trier par « plus
// récente » ou par « ce qui réclame une action » n'y aurait aucun sens.
export const TRIS_LIGNES = [
  { id: 'montant', cle: 'finances.triMontantOp' },
  { id: 'libelle', cle: 'finances.tri_libelle' },
];

// Filtre/tri des OCCURRENCES d'un plan. Distinct de `filtrerOccurrences`, qui
// trie sur le réel et l'écart — absents ici, où l'on ne montre que du prévu.
export function filtrerPlan(occurrences = [], { recherche = '', sens = 'tout', etat = 'tout', tri = 'montant' } = {}) {
  const q = normaliser(recherche).trim();
  let out = occurrences.filter(o => {
    if (!o) return false;
    const estRevenu = o.ligne?.sens === 'revenu';
    if (sens === 'revenu' && !estRevenu) return false;
    if (sens === 'depense' && estRevenu) return false;
    if (etat !== 'tout' && etatOccurrence(o) !== etat) return false;
    if (!q) return true;
    return normaliser(o.ligne?.libelle).includes(q) || correspondMontant(o.prevu, q);
  });
  out = [...out];
  if (tri === 'libelle') out.sort((a, b) => (a.ligne?.libelle || '').localeCompare(b.ligne?.libelle || ''));
  else out.sort((a, b) => Math.abs(b.prevu) - Math.abs(a.prevu));
  return out;
}

// Filtre par ÉTAT de rattachement d'une occurrence (Cédric, 11/08 : « une
// recherche sur les opérations prévues non rattachées, partiellement ou
// rattachées »). Distinct du filtre par sens : on croise les deux.
//   'tout' | 'libre' (rien de rattaché) | 'partielle' | 'rattachee'
export const ETATS_PLAN = [
  { id: 'tout',      cle: 'finances.tousBudgets' },
  { id: 'libre',     cle: 'finances.st_manquante' },
  { id: 'partielle', cle: 'finances.st_partielle' },
  { id: 'rattachee', cle: 'finances.st_realisee' },
];

// L'état d'une occurrence du point de vue du RATTACHEMENT — plus grossier que
// son statut : ce qui compte ici est « y a-t-il quelque chose d'affecté, et
// est-ce complet ? », pas la nuance soldée/ignorée/absente.
export function etatOccurrence(o) {
  if (!o) return 'libre';
  if (!o.nb) return 'libre';
  return Math.abs(o.reel || 0) + 0.005 < Math.abs(o.prevu || 0) ? 'partielle' : 'rattachee';
}
